import React, { useState, useRef, useEffect, useCallback } from "react";
import jsQR from "jsqr";
import { Dialog } from "@mui/material";
import { X } from "lucide-react";
import { toast } from "sonner";
import { useAddQrCodeMutation } from "../../../../redux/service/vendorService";

const DealQRScanner = ({ isOpen, onClose }) => {
  const [outputMessage, setOutputMessage] = useState(
    isOpen
      ? "Initializing camera... Please grant permission."
      : "Scanner is closed"
  );
  const [isCameraError, setIsCameraError] = useState(false);

  const [submitQrDetails, submitQrDetailsInfo] = useAddQrCodeMutation();

  const videoRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const captureCanvasRef = useRef(document.createElement("canvas"));
  const requestRef = useRef(null);
  const lastScanResultRef = useRef(null);
  const scanCooldownRef = useRef(false);
  const streamRef = useRef(null);

  // Reset scanner state when closing
  const handleClose = useCallback(() => {
    setOutputMessage("Scanner is closed");
    onClose();
  }, [onClose]);

  const drawBoundingBox = useCallback((location) => {
    const canvas = overlayCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    ctx.beginPath();
    ctx.moveTo(location.topLeftCorner.x, location.topLeftCorner.y);
    ctx.lineTo(location.topRightCorner.x, location.topRightCorner.y);
    ctx.lineTo(location.bottomRightCorner.x, location.bottomRightCorner.y);
    ctx.lineTo(location.bottomLeftCorner.x, location.bottomLeftCorner.y);
    ctx.closePath();
    ctx.lineWidth = 4;
    ctx.strokeStyle = "#4ade80";
    ctx.stroke();
  }, []);

  const scanLoop = useCallback(async () => {
    if (!isOpen) return; // Don't scan if dialog is closed

    const video = videoRef.current;
    const overlayCanvas = overlayCanvasRef.current;
    const captureCanvas = captureCanvasRef.current;

    if (video && video.readyState === video.HAVE_ENOUGH_DATA && overlayCanvas) {
      const overlayCtx = overlayCanvas.getContext("2d");
      const captureCtx = captureCanvas.getContext("2d");

      // Set canvas dimensions to match video
      const height = video.videoHeight;
      const width = video.videoWidth;
      overlayCanvas.height = height;
      overlayCanvas.width = width;
      captureCanvas.height = height;
      captureCanvas.width = width;

      // Capture frame for processing
      captureCtx.drawImage(video, 0, 0, width, height);
      const imageData = captureCtx.getImageData(0, 0, width, height);

      // Process with jsQR
      const code = jsQR(imageData.data, width, height, {
        inversionAttempts: "dontInvert",
      });

      // Clear previous frame
      overlayCtx.clearRect(0, 0, width, height);

      if (code) {
        drawBoundingBox(code.location);

        if (
          !scanCooldownRef.current &&
          code.data !== lastScanResultRef.current
        ) {
          lastScanResultRef.current = code.data;
          scanCooldownRef.current = true;

          setOutputMessage("QR Code Detected!");
          const JsonData = code.data ? JSON.parse(code.data) : {};
          try {
            if (JsonData?.purchase_id) {
              try {
                const response = await submitQrDetails({
                  id: code.data ? JSON.parse(code.data)?.purchase_id : "Find",
                  body: { ...JsonData, action: "FULFILL" },
                }).unwrap();
                toast.success(response?.message || "QR scan successfully!");
                onClose();
              } catch (error) {
                toast.error(
                  error?.data?.error ? error?.data?.error : "Failed to scan QR."
                );
              }
            }
          } catch (e) {
            console.log(code.data);
          }

          // Reset after 2 seconds
          setTimeout(() => {
            scanCooldownRef.current = false;
            lastScanResultRef.current = null;
          }, 2000);
        }
      } else if (!scanCooldownRef.current) {
        setOutputMessage("Searching for QR code...");
      }
    }

    requestRef.current = requestAnimationFrame(scanLoop);
  }, [isOpen, drawBoundingBox, submitQrDetails, onClose]);

  useEffect(() => {
    if (!isOpen) {
      // Clean up if scanner is closed
      cancelAnimationFrame(requestRef.current);
      const stream = streamRef.current;
      const video = videoRef.current;
      
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      if (video) {
        video.srcObject = null;
      }
      return;
    }

    let mounted = true;
    setOutputMessage("Initializing camera... Please grant permission.");
    setIsCameraError(false);

    navigator.mediaDevices
      .getUserMedia({
        video: {
          facingMode: "environment",
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      })
      .then((stream) => {
        if (!mounted) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = stream;
        const video = videoRef.current;

        if (!video) return;

        video.srcObject = stream;
        video.setAttribute("playsinline", "true");

        const handlePlayError = (error) => {
          console.error("Video play error:", error);
          if (!mounted) return;
          setIsCameraError(true);
          setOutputMessage("Error: Could not start camera. Try refreshing.");
        };

        video.addEventListener("error", handlePlayError);

        video
          .play()
          .then(() => {
            if (mounted) {
              setOutputMessage("Searching for QR code...");
              requestRef.current = requestAnimationFrame(scanLoop);
            }
          })
          .catch(handlePlayError);

        return () => {
          video.removeEventListener("error", handlePlayError);
        };
      })
      .catch((err) => {
        console.error("Camera access error:", err);
        if (mounted) {
          setIsCameraError(true);
          setOutputMessage(
            "Error: Camera access denied. Please check permissions."
          );
        }
      });

    return () => {
      mounted = false;
      cancelAnimationFrame(requestRef.current);
      const stream = streamRef.current;
      const video = videoRef.current;
      
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      if (video) {
        video.srcObject = null;
      }
    };
  }, [isOpen, scanLoop]);

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="sm">
      {submitQrDetailsInfo?.isLoading && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col items-center justify-center z-10">
          <div className="w-10 h-10 border-4 border-white border-t-transparent rounded-full animate-spin mb-3"></div>
          <p className="text-white text-sm font-medium">Verifying the QR...</p>
        </div>
      )}
      <div className="flex justify-end p-1">
        <button
          onClick={handleClose}
          className="bg-gray-400 hover:bg-gray-500 transition-colors rounded-full p-1 text-white"
          aria-label="Close scanner"
        >
          <X size={20} />
        </button>
      </div>

      <div className="text-white flex flex-col items-center justify-center px-4 pb-4 font-sans">
        <div className="w-full mx-auto">
          <p className="text-gray-400 text-center">
            Point a QR code at the camera
          </p>
          <p
            className={`text-lg text-center font-medium my-2  ${
              isCameraError ? "text-red-400" : "text-gray-400"
            }`}
          >
            {outputMessage}
          </p>
          <div className="relative w-full aspect-[4/3] bg-gray-800 rounded-lg overflow-hidden shadow-lg mb-4 mx-auto h-[300px]">
            <video
              ref={videoRef}
              className="absolute top-0 left-0 w-full h-full object-cover"
              muted
              playsInline
            />
            <canvas
              ref={overlayCanvasRef}
              className="absolute top-0 left-0 w-full h-full pointer-events-none"
            />
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default DealQRScanner;